import { HIGHLIGHTED_PRICE_MAX_ITEMS } from '@/features/Dashboard/constants/const';
import type {
  MarketApp,
  MarketAppWithTabs,
  TileBigNumber,
  WidgetV2
} from '@/features/Dashboard/types';

type TilesWidget = Extract<MarketApp, { type: 'tiles' }>;
type TabsWidget = MarketAppWithTabs;

type EditActions = {
  updateCurrentTabTiles: (tiles: TileBigNumber[]) => void;
  updateAppTab: (currentTabId: string, appId: string, tabId: string, updatedTab: WidgetV2) => void;
  addApp: (currentTabId: string, widget: MarketAppWithTabs) => void;
  addTabToApp: (currentTabId: string, appId: string, tab: WidgetV2) => void;
};

type EditingTarget = {
  appId?: string;
  tabId?: string;
  tileId?: string;
} | null;

export const isTilesWidget = (widget: MarketApp): widget is TilesWidget =>
  'type' in widget && widget.type === 'tiles' && 'tiles' in widget;

export const isTabsWidget = (widget: MarketApp): widget is TabsWidget =>
  !('type' in widget) && Array.isArray(widget.tabs);

export const wouldExceedTilesLimit = (widget: MarketApp, currentTilesCount: number) => {
  if (!isTilesWidget(widget)) return false;

  const newTilesCount = widget.tiles.length;
  const hasTilesAlready = currentTilesCount > 0;
  return hasTilesAlready
    ? newTilesCount > HIGHLIGHTED_PRICE_MAX_ITEMS
    : currentTilesCount + newTilesCount > HIGHLIGHTED_PRICE_MAX_ITEMS;
};

// Dispatches the right Redux action based on what editingTarget encodes.
// See DrawerStepsContext for the encoding scheme (null / appId-only / appId+tabId).
export const handleEditModeSave = ({
  actions,
  widget,
  currentTabId,
  editingTarget,
  setEditingTarget,
  closeDrawer
}: {
  actions: EditActions;
  widget: MarketApp;
  currentTabId: string | null;
  editingTarget: EditingTarget;
  setEditingTarget: (target: EditingTarget) => void;
  closeDrawer: () => void;
}) => {
  // Tiles always replace the entire highlighted-price row regardless of editingTarget.
  if (isTilesWidget(widget)) {
    actions.updateCurrentTabTiles(widget.tiles);
    setEditingTarget(null);
    closeDrawer();
    return;
  }

  // appId + tabId → replace the existing tab in place.
  if (editingTarget?.appId && editingTarget.tabId && isTabsWidget(widget) && currentTabId) {
    const updatedTab = widget.tabs[0] as WidgetV2 | undefined;
    if (updatedTab) {
      actions.updateAppTab(currentTabId, editingTarget.appId, editingTarget.tabId, updatedTab);
      setEditingTarget(null);
      closeDrawer();
      return;
    }
  }

  // appId only → add the selected app as a new tab inside the target widget.
  if (editingTarget?.appId && !editingTarget.tabId && isTabsWidget(widget) && currentTabId) {
    const newTab = widget.tabs[0] as WidgetV2 | undefined;
    if (newTab) {
      actions.addTabToApp(currentTabId, editingTarget.appId, newTab);
      setEditingTarget(null);
      closeDrawer();
      return;
    }
  }

  // null editingTarget → create a new top-level widget on the current dashboard tab.
  if (isTabsWidget(widget) && currentTabId) {
    actions.addApp(currentTabId, widget);
  } else if (isTilesWidget(widget)) {
    actions.updateCurrentTabTiles(widget.tiles);
  }

  closeDrawer();
};
